import type {
    NewWorkflow,
    Step,
    Workflow,
    WorkflowConfig,
    WorkflowExecutionSearchRequest,
    WorkflowListRequest,
    WorkflowListResponse
} from '../types/workflows';
import { WorkflowType } from '../types/workflows';
import {
    type ExecutionControlParameters, ExecutionHandlingStatus,
    type WorkflowExecutionRequestHeaders,
    type WorkflowExecutionResponse,
    type WorkflowExecutionResult, type WorkflowExecutionResultData,
    type WorkflowExecutionResultsResponse
} from '../types/workflow-execution';
import { type WorkflowExecutionM3LogsResponse } from '../types/workflow-m3-logs';
import type { ExcelData, UserRowSelection } from '../types/excel-preview';
import { resourceGroup } from '../constants/workflows';
import { getExecutionControlParameters } from '../utils/workflows-execution';
import { BucketType, WorkflowFileType, copyFile, getSignedUrl } from '../api/files';
import { getBaseUrl } from 'vince-core/utils/env';
import { APIClient } from 'vince-core/api/apiClient';
import { VL_API_BASE_URL } from 'vince-core/constants/backendUrls';

const baseUrl = `${VL_API_BASE_URL}/workflows`;
const baseLiveUrl = `${VL_API_BASE_URL}/live/workflows`;
const v1Url = `${VL_API_BASE_URL}/v1/workflows`;
const getUtilsUrl = () => getBaseUrl('/v2/utils');

// Some workflow API environments return workflowConfig.steps as serialized JSON even
const normalizeWorkflowResponse = (workflow: Workflow): Workflow => {
    const steps = workflow.definition?.workflowConfig.steps as unknown;
    if (typeof steps !== 'string') {
        return workflow;
    }

    const parsedSteps: unknown = JSON.parse(steps);
    if (!Array.isArray(parsedSteps)) {
        throw new TypeError('Invalid workflow steps received from the API.');
    }

    workflow.definition!.workflowConfig.steps = parsedSteps as Step[];
    return workflow;
};

export async function deleteWorkflow (workflowId: string): Promise<void> {
    await APIClient(`${baseUrl}/${workflowId}`, { method: 'DELETE' });
}

export async function workflowExecutions (workflowId: string): Promise<WorkflowExecutionResultsResponse> {
    return await APIClient<WorkflowExecutionResultsResponse>(`${v1Url}/${workflowId}/results?limit=2000`);
}


export async function searchWorkflowExecutions (request: WorkflowExecutionSearchRequest): Promise<WorkflowExecutionResultsResponse> {
    const qs = new URLSearchParams();

    let search = '*';
    qs.set('limit', request.limit?.toString() ?? '1000');

    if (request.search) {
        search = request.search + '*'; // trailing wildcard as default
    }
    if (typeof request.failed === 'boolean') {
        search += '* AND failed:' + request.failed.toString();
    }
    qs.set('search', search);

    return await APIClient<WorkflowExecutionResultsResponse>(`${v1Url}/${request.workflowId}/results?${qs.toString()}`);
}

export async function workflowExecutionData (workflowId: string, executionId: string, headers?: Record<string, string>): Promise<WorkflowExecutionResultData> {
    return await APIClient<WorkflowExecutionResultData>(`${v1Url}/${workflowId}/results/${executionId}/data`, { headers: headers ?? {} });
}

export async function workflowExecutionDetails (workflowId: string, executionId: string): Promise<WorkflowExecutionResult> {
    return await APIClient<WorkflowExecutionResult>(`${v1Url}/${workflowId}/results/${executionId}`);
}

export async function updateExecutionDetails (workflowId: string, executionId: string, handlingStatus: ExecutionHandlingStatus): Promise<WorkflowExecutionResult> {
    return await APIClient<WorkflowExecutionResult>(`${v1Url}/${workflowId}/results/${executionId}`, {
        method: 'PATCH',
        body: JSON.stringify({
            handlingStatus
        })
    });
}

export async function workflowExecutionLogs (workflowId: string, executionId: string): Promise<WorkflowExecutionM3LogsResponse> {
    return await APIClient<WorkflowExecutionM3LogsResponse>(`${baseLiveUrl}/${workflowId}/logs/${executionId}`);
}

export async function loadWorkflow (workflowId: string): Promise<Workflow> {
    return normalizeWorkflowResponse(await APIClient<Workflow>(`${baseUrl}/${workflowId}`));
}

// Caches workflow loads by id so repeated lookups (e.g. notifications, which are
// mostly the same workflow run over and over) reuse a single request instead of
// re-fetching. The promise itself is cached, so concurrent calls for the same id
// share one in-flight request rather than firing duplicate calls.
const workflowCache = new Map<string, Promise<Workflow>>();

export function loadWorkflowCached (workflowId: string): Promise<Workflow> {
    let cached = workflowCache.get(workflowId);
    if (!cached) {
        cached = loadWorkflow(workflowId).catch((err) => {
            // Don't cache failures — allow a retry on the next call.
            workflowCache.delete(workflowId);
            throw err;
        });
        workflowCache.set(workflowId, cached);
    }
    return cached;
}

export async function getFileDownloadUrl (filename: string) {
    const response = await getSignedUrl(filename, BucketType.WORKFLOW);
    return response.url;
}

export async function loadWorkflows (request: WorkflowListRequest | null = null) {
    let res = [] as Workflow[];

    let leftToLoad = request?.limit || 1000000;

    let response: WorkflowListResponse | null = null;
    do {
        const queryParams = new URLSearchParams();

        queryParams.set('limit', Math.min(leftToLoad || 1000, 1000).toString());

        if (request?.withStepType) {
            queryParams.set('search', `overview.steps.type:${request?.withStepType}`);
        }

        if (response?.lastKey) {
            queryParams.set('startKey', response.lastKey);
        }
        response = await APIClient<WorkflowListResponse>(`${baseUrl}?${queryParams.toString()}`);
        res = [...res, ...response.items.map(normalizeWorkflowResponse)];

        if (leftToLoad && request && res.length >= request.limit!) {
            break;
        }

        if (leftToLoad) {
            leftToLoad = leftToLoad - response.items.length;
        }
    } while (response?.lastKey);

    res = res.filter((x: Workflow) => {
        return x.resourceGroup !== resourceGroup.SYSTEM && x.resourceGroup !== resourceGroup.APP;
    });
    return res;
}

function encodeHeaderParam (value: WorkflowExecutionRequestHeaders | undefined): string {
    const stringified = Object.values(value ?? {}).join('');
    const hasSpecialChar = [...stringified].some(char => char.charCodeAt(0) > 127);
    if (hasSpecialChar) {
        const bytes = new TextEncoder().encode(JSON.stringify(value ?? {}));
        return btoa(String.fromCharCode(...bytes));
    }
    return JSON.stringify(value ?? {});
}

function buildExecutionRequestHeaders (headerParams: WorkflowExecutionRequestHeaders | undefined, controlParams: ExecutionControlParameters | undefined) {
    return {
        'vl-wf-control': JSON.stringify(controlParams ?? {}),
        'vl-wf-header': encodeHeaderParam(headerParams)
    };
}

export async function executeSync (workflowId: string, headerParams: WorkflowExecutionRequestHeaders | undefined, controlParams: ExecutionControlParameters | undefined, body: object) {
    return await APIClient<WorkflowExecutionResponse>(`${baseLiveUrl}/${workflowId}/sync`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: buildExecutionRequestHeaders(headerParams, Object.assign({}, controlParams, { returnData: true }))
    });
}

export async function executeAsync (workflowId: string, headerParams: WorkflowExecutionRequestHeaders | undefined, controlParams: ExecutionControlParameters | undefined, body: object) {
    return await APIClient<WorkflowExecutionResponse>(`${baseLiveUrl}/${workflowId}/async`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: buildExecutionRequestHeaders(headerParams, controlParams)
    });
}

export function rerunExecution (workflowId: string, executionResult: WorkflowExecutionResult, workflowConfig: WorkflowConfig): Promise<WorkflowExecutionResponse> {
    const controlParams = getExecutionControlParameters(executionResult, workflowConfig);
    const headers = executionResult.payload?.trigger?.header as unknown as WorkflowExecutionRequestHeaders || {};
    const body = executionResult.payload?.trigger?.body || {};
    return executeAsync(workflowId, headers, controlParams, body);
}

export async function updateWorkflow (workflow: Partial<Workflow>) {
    return normalizeWorkflowResponse(await APIClient<Workflow>(`${baseUrl}`, {
        method: 'PUT',
        body: JSON.stringify(workflow)
    }));
}

export async function createWorkflow (workflow: NewWorkflow) {
    return normalizeWorkflowResponse(await APIClient<Workflow>(`${baseUrl}`, {
        method: 'POST',
        body: JSON.stringify(Object.assign({}, workflow, {
            workflowId: 'WORKFLOW-' + generateId(false),
            workflowAlias: workflow.workflowName
        }))
    }));
}

export async function searchSystemWorkflow (workflowName: string) {
    return await APIClient<{items: Record<string, string>}>(`${baseUrl}/?search=workflowName:${workflowName}`, {
        method: 'GET',
        headers: {
            'vl-response-transformation': '{ "items": items.{ "name": workflowAlias, "workflowId": workflowId, "description": description}}'
        }
    });
}

export async function getWorkflowLabels () {
    const response = await APIClient<{ items: string[] }>(`${v1Url}/labels`);
    return response.items;
}
function rewriteWorkflowFileKey (key: string, oldWorkflowId: string, newWorkflowId: string, fileType: WorkflowFileType): string {
    const segments = key.split('/');
    const idx = segments.indexOf(oldWorkflowId);
    if (idx !== -1) {
        segments[idx] = newWorkflowId;
        return segments.join('/');
    }
    // Legacy key (tenant-prefixed, no workflowId segment) — normalize to the modern {workflowId}/{type}/{file} convention.
    const filename = segments[segments.length - 1] ?? key;
    return `${newWorkflowId}/${fileType}/${filename}`;
}

export async function copyWorkflow (
    workflowId: string,
    workflowName: string,
    workflowType?: WorkflowType,
    enableLongRunning?: boolean
): Promise<Workflow> {
    const original = await loadWorkflow(workflowId);
    if (!original.definition) {
        throw new Error('Workflow definition is missing and required for duplication.');
    }

    const resolvedWorkflowType = workflowType ?? original.workflowType ?? WorkflowType.STANDARD;
    const definition = JSON.parse(JSON.stringify(original.definition));
    definition.workflowConfig.enableLongRunning = resolvedWorkflowType === WorkflowType.STANDARD
        ? enableLongRunning ?? original.definition.workflowConfig.enableLongRunning ?? false
        : false;

    const newWorkflow: NewWorkflow = {
        active: original.active ?? true,
        definition,
        description: original.description ?? '',
        hasError: original.hasError ?? false,
        labels: original.labels ?? [],
        tags: original.tags ?? {},
        tenantId: original.tenantId ?? '',
        workflowAlias: workflowName,
        workflowName,
        entity: original.entity ?? '',
        workflowType: resolvedWorkflowType
    };
    const created = await createWorkflow(newWorkflow);
    const newWorkflowId = created.workflowId;
    const cfg = created.definition?.workflowConfig;
    if (!newWorkflowId || !cfg) { return created; }

    // Only the design file is copied: it's the only one with a user-triggered delete action
    let filesCopied = false;
    if (cfg.excelDesignFileKey) {
        const destinationKey = rewriteWorkflowFileKey(cfg.excelDesignFileKey, workflowId, newWorkflowId, WorkflowFileType.DESIGN);
        if (destinationKey !== cfg.excelDesignFileKey) {
            const res = await copyFile({ sourceKey: cfg.excelDesignFileKey, destinationKey, bucketType: BucketType.TENANT });
            cfg.excelDesignFileKey = res.destinationKey;
            filesCopied = true;
        }
    }

    if (filesCopied) {
        return await updateWorkflow({
            tenantId: created.tenantId,
            workflowId: newWorkflowId,
            definition: created.definition
        });
    }
    return created;
}

export async function fetchExcelMetadata (excelFile: File, previewRows: number = 20, includeStyles = false, m3apiParams?: object, userRowSelection?: UserRowSelection): Promise<ExcelData> {
    const formData = new FormData();
    formData.append('excel', excelFile);
    formData.append('previewRows', previewRows.toString());
    formData.append('includeStyles', includeStyles.toString());

    if (m3apiParams) {
        formData.append('m3ApiParams', JSON.stringify(m3apiParams));
    }

    if (userRowSelection) {
        formData.append('userRowSelection', JSON.stringify(userRowSelection));
    }

    return await APIClient<ExcelData>(`${getUtilsUrl()}/convert/excel-to-metadata`, {
        method: 'POST',
        body: formData
    });
}
